import QueryState from '../../components/query_state'
import { EmptyState } from '@cinesend/ui-kit'
import DataTable from '../../components/data_table'
import AppDialog from '../../components/app_dialog'
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import axios from 'axios'
import useTableFilters from '../../hooks/use_table_filters'
import { useSnackbar } from '../../hooks/use_snackbar'
import { useParams } from 'react-router-dom'

const AddModal = ({ onClose, type, queryKey, category, invalidateKey, title: titleOverride }) => {
  const { addMessage } = useSnackbar()
  const [selectedIDs, setSelectedIDs] = useState([])
  const { data: items, isLoading, isError, error } = useQuery(queryKey)
  const showWarningSlateAssetFilters = type === 'warning-slates' && !queryKey?.[0].includes('&adAssignment')
  const { cycleID } = useParams()
  const { data: cycle } = useQuery([`cycles/${cycleID}`])
  const {
    data: ratingCollection,
    isInitialLoading: isRatingCollectionLoading,
    isError: isRatingCollectionError,
    error: ratingCollectionError,
  } = useQuery({
    queryKey: [`/collections?schema_id=${cycle?.schema_id}&search=Ratings`],
    enabled: showWarningSlateAssetFilters && !!cycle?.id,
  })
  const ratingCollectionId = ratingCollection?.[0]?.id
  const {
    data: ratings,
    isInitialLoading: isRatingsLoading,
    isError: isRatingError,
    error: ratingError,
  } = useQuery({
    queryKey: [`collection-values?collectionID=${ratingCollectionId}`],
    enabled: showWarningSlateAssetFilters && !!ratingCollectionId,
  })
  const noun = ['assets', 'warning-slates', 'ad-pools', 'interstitial'].includes(type) ? 'assets' : 'categories'
  const title = titleOverride ?? `Add ${noun}`
  const queryClient = useQueryClient()

  const filteringOptions = showWarningSlateAssetFilters
    ? [
        {
          label: 'Rating',
          key: 'rating',
          type: 'checkboxes',
          options: ratings?.map((rating) => ({ label: rating.label, key: rating.id, value: rating.id })) ?? [],
        },
      ]
    : null

  const { filtering, searching } = useTableFilters({ filteringOptions, enableSearch: true })

  const filteredItems = items
    ?.filter((item) => {
      if (searching.search) {
        const search = searching.search.toLowerCase()
        return (
          item.title?.toLowerCase().includes(search) ||
          item.category_values?.some((v) => v.value?.toLowerCase().includes(search))
        )
      }
      return true
    })
    .filter((item) => {
      if (filtering?.filters?.rating?.length) {
        return filtering.filters.rating.map(String).includes(item.field_values?.[0]?.value)
      }
      return true
    })

  const isFeaturedContent = queryKey?.[0].includes('&featuredContentAssignment')
  const addItems = useMutation(
    () => {
      return axios.post(`/categories/${category.id}/add-items`, {
        type,
        ids: selectedIDs,
        isFeaturedContent,
      })
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(invalidateKey)
        onClose()
      },
      onError: (err) => {
        addMessage(err?.response?.data?.error ?? err.message, 'error')
      },
    }
  )

  const toggleSelectedID = (id) => {
    selectedIDs.includes(id)
      ? setSelectedIDs(selectedIDs.filter((i) => i !== id))
      : setSelectedIDs([...selectedIDs, id])
  }

  const columns = [
    {
      key: 'name',
      header: 'Name',
      render: (data) => data.title ?? data.category_values?.[0]?.value,
    },
    {
      key: 'plural',
      header: showWarningSlateAssetFilters ? 'Rating' : 'Asset Type',
      render: (data) =>
        showWarningSlateAssetFilters
          ? ratings?.find((r) => r.id == data.field_values?.[0]?.value)?.label
          : data.plural_label,
    },
  ]

  return (
    <AppDialog
      title={title}
      onClose={onClose}
      size='lg'
      confirmText='Add'
      onConfirm={() => addItems.mutate()}
      confirmDisabled={selectedIDs.length === 0}
    >
      <QueryState
        isLoading={showWarningSlateAssetFilters && (isRatingsLoading || isRatingCollectionLoading)}
        error={
          showWarningSlateAssetFilters && (isRatingCollectionError || isRatingError)
            ? (ratingCollectionError ?? ratingError)
            : null
        }
      >
        <div className={'max-h-96 overflow-y-scroll'}>
          <DataTable
            caption={noun}
            columns={columns}
            rows={filteredItems ?? []}
            onRowClick={(data) => toggleSelectedID(data.id)}
            isLoading={isLoading}
            error={isError ? error : null}
            empty={
              <EmptyState title={`No ${noun.toLowerCase()} found!`} detail={`Create ${noun.toLowerCase()} to begin.`} />
            }
            searching={searching}
            filtering={filtering}
            selectedKeys={selectedIDs.map(String)}
            onSelectionChange={(keys) =>
              setSelectedIDs(filteredItems.filter(({ id }) => keys.includes(String(id))).map(({ id }) => id))
            }
            selectionLabel={(data) => `Select ${data.title ?? data.id}`}
          />
        </div>
      </QueryState>
    </AppDialog>
  )
}

export default AddModal
